import React, {useState, useEffect, useRef, useCallback} from 'react';
import './Chat.css';
import {fetchChats, sendMessage, getMessages, updateMessage, deleteMessage} from '../utils/api';
import {ADMIN_ROLE} from '../utils/roles';
import CreateGroupChatModal from './CreateGroupChatModal';
import EditGroupChatModal from './EditGroupChatModal';

const Chat = ({user, roles, token, isChatOpen, setChatOpen, selectedChat, setSelectedChat, chatSize, setChatSize}) => {
    const [chats, setChats] = useState([]);
    const [messages, setMessages] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [newMessage, setNewMessage] = useState('');
    const [minimized, setMinimized] = useState(true);
    const messagesEndRef = useRef(null);
    const ws = useRef(null);
    const [editingMessage, setEditingMessage] = useState(null);
    const [editedText, setEditedText] = useState('');
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [isEditGroupChatModalOpen, setEditGroupChatModalOpen] = useState(false);
    const [isCreateGroupChatModalOpen, setCreateGroupChatModalOpen] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);

    const [isFetching, setIsFetching] = useState(false);

    const handleChatUpdated = () => {
        fetchChats().then(response => {
            const updatedChats = response.data;
            setChats(updatedChats);
            if (selectedChat) {
                const updatedSelectedChat = updatedChats.find(chat => chat.id === selectedChat.id);
                if (updatedSelectedChat) {
                    setSelectedChat(updatedSelectedChat);
                }
            }
        });
    };

    const handleChatCreated = (newChatId) => {
        fetchChats().then(data => {
            setChats(data.data);
            const newChat = data.data.find(chat => chat.id === newChatId);
            if (newChat) {
                handleSelectChat(newChat);
            }
        });
    };

    useEffect(() => {
        if (roles) {
            setIsAdmin(roles.some(role => role.id === ADMIN_ROLE));
        }
    }, [roles]);

    const startResizing = useCallback((mouseDownEvent) => {
        const startSize = chatSize;
        const startPosition = {x: mouseDownEvent.clientX, y: mouseDownEvent.clientY};
        document.body.style.userSelect = 'none';

        function onMouseMove(mouseMoveEvent) {
            setChatSize({
                width: startSize.width - (mouseMoveEvent.clientX - startPosition.x),
                height: startSize.height - (mouseMoveEvent.clientY - startPosition.y),
            });
        }
        function onMouseUp() {
            document.body.removeEventListener("mousemove", onMouseMove);
            document.body.style.userSelect = 'auto';
        }

        document.body.addEventListener("mousemove", onMouseMove);
        document.body.addEventListener("mouseup", onMouseUp, { once: true });
    }, [chatSize, setChatSize]);

    const scrollToBottom = (behavior = 'smooth') => {
        messagesEndRef.current?.scrollIntoView({ behavior });
    }

    useEffect(() => {
        scrollToBottom('auto');
    }, [selectedChat]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (activeDropdown && !event.target.closest('.message-actions')) {
                setActiveDropdown(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [activeDropdown]);

    useEffect(() => {
        setMinimized(!isChatOpen);
    }, [isChatOpen]);

    const connect = useCallback((chatId) => {
        if (ws.current) {
            ws.current.close();
        }

        ws.current = new WebSocket(`ws://${window.location.host}/ws/chats/${chatId}?token=${token}`);

        ws.current.onmessage = (event) => {
            const message = JSON.parse(event.data);
            setMessages((prevMessages) => {
                const existingMessageIndex = prevMessages.findIndex(msg => msg.id === message.id);

                if (existingMessageIndex !== -1) {
                    // If the message text is empty, it means it was deleted.
                    if (message.text === "") {
                        const newMessages = [...prevMessages];
                        newMessages.splice(existingMessageIndex, 1);
                        return newMessages;
                    }
                    // Otherwise, it was updated.
                    const newMessages = [...prevMessages];
                    newMessages[existingMessageIndex] = message;
                    return newMessages;
                }

                // It's a new message
                return [...prevMessages, message];
            });
        };
    }, [token]);

    useEffect(() => {
        if (!minimized) {
            fetchChats().then(data => setChats(data.data));
        } else if (ws.current) {
            ws.current.close();
        }
    }, [minimized]);

    const messagesContainerRef = useRef(null);

    const fetchMoreMessages = useCallback(() => {
        if (!hasMore || isFetching) {
            return;
        }
        setIsFetching(true);
        getMessages(selectedChat.id, page, 100).then(data => {
            const receivedMessages = (data.data || []).slice().reverse();

            const messagesContainer = messagesContainerRef.current;
            const oldScrollHeight = messagesContainer.scrollHeight;

            setMessages(prevMessages => [...receivedMessages, ...prevMessages]);

            if (messagesContainer) {
                messagesContainer.scrollTop = messagesContainer.scrollHeight - oldScrollHeight;
            }

            setPage(prevPage => prevPage + 1);
            if (receivedMessages.length < 30) {
                setHasMore(false);
            }
            setIsFetching(false);
        });
    }, [page, hasMore, selectedChat, isFetching]);

    useEffect(() => {
        const handleScroll = () => {
            if (messagesContainerRef.current && messagesContainerRef.current.scrollTop < 200) {
                fetchMoreMessages();
            }
        };

        const container = messagesContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
        }

        return () => {
            if (container) {
                container.removeEventListener('scroll', handleScroll);
            }
        };
    }, [fetchMoreMessages]);

    useEffect(() => {
        if (selectedChat && selectedChat.id) {
            setMessages([]);
            setPage(1);
            setHasMore(true);
            getMessages(selectedChat.id, 1, 100).then(data => {
                const receivedMessages = (data.data || []).slice().reverse();
                setMessages(receivedMessages);
                setPage(2);
                if ((receivedMessages.length || 0) < 30) {
                    setHasMore(false);
                }
                setTimeout(() => scrollToBottom('auto'), 100);

                if (receivedMessages.length === 0) {
                    setChats(prevChats => {
                        if (!prevChats.some(c => c.id === selectedChat.id)) {
                            return [selectedChat, ...prevChats];
                        }
                        return prevChats;
                    });
                }
            });
            connect(selectedChat.id);
        } else {
            setMessages([]);
            if (ws.current) {
                ws.current.close();
            }
        }

        return () => {
            if (ws.current) {
                ws.current.close();
            }
        }
    }, [selectedChat, connect]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden' && ws.current) {
                ws.current.close();
            } else if (document.visibilityState === 'visible' && selectedChat && selectedChat.id) {
                connect(selectedChat.id);
            }
        };

        window.addEventListener('beforeunload', () => {
            if (ws.current) {
                ws.current.close();
            }
        });

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [selectedChat, connect]);

    useEffect(() => {
        const textarea = document.querySelector('.chat-input textarea');
        if (textarea) {
            textarea.style.height = 'auto';
            textarea.style.height = `${textarea.scrollHeight}px`;
        }
    }, [newMessage]);

    useEffect(() => {
        const textarea = document.querySelector('.edit-message-container textarea');
        if (textarea) {
            textarea.style.height = 'auto';
            textarea.style.height = `${textarea.scrollHeight}px`;
        }
    }, [editedText]);


    const handleSelectChat = (chat) => {
        setSelectedChat(chat);
        setChats(prevChats => prevChats.map(c => {
            if (c.id === chat.id) {
                return {...c, unread_count: 0};
            }
            return c;
        }));
    };

    const handleSendMessage = () => {
        if (newMessage.trim() && selectedChat) {
            sendMessage(selectedChat.id, newMessage);
            setNewMessage('');
            scrollToBottom();
        }
    };

    const handleUpdateMessage = async (messageId) => {
        if (editedText.trim() && selectedChat) {
            await updateMessage(selectedChat.id, messageId, editedText);
            setEditingMessage(null);
            setEditedText('');
        }
    };

    const handleDeleteMessage = async (messageId) => {
        if (selectedChat) {
            await deleteMessage(selectedChat.id, messageId);
        }
    };

    const handleEditClick = (message) => {
        setEditingMessage(message.id);
        setEditedText(message.text);
        setActiveDropdown(null);
    };

    if (minimized) {
        return (
            <div className="chat-minimized" onClick={() => setChatOpen(true)}>
                Chat
            </div>
        );
    }

    return (
        <div className="chat-window" style={{width: chatSize.width, height: chatSize.height}}>
            <div className="resizer-tl" onMouseDown={startResizing}></div>
            <div className="chat-sidebar">
                {chats.map((chat) => (
                    <div key={chat.id}
                         className={`chat-sidebar-item ${selectedChat && selectedChat.id === chat.id ? 'selected-chat' : ''}`}
                         onClick={() => handleSelectChat(chat)}>
                        {chat.name}
                        {chat.unread_count > 0 && <span className="unread-count">{chat.unread_count}</span>}
                    </div>
                ))}
                {isAdmin && (
                    <div className="create-group-chat-button-container">
                        <button className="text-button create-group-chat-button" onClick={() => setCreateGroupChatModalOpen(true)}>Create Group Chat</button>
                    </div>
                )}
            </div>
            <div className="chat-main">
                <div className="chat-header">
                    <span>{selectedChat ? selectedChat.name : 'Chat'}</span>
                    <div className="chat-header-buttons">
                        {isAdmin && selectedChat && selectedChat.is_group && (
                            <button onClick={() => setEditGroupChatModalOpen(true)} className="three-dots">
                                ...
                            </button>
                        )}
                        <button onClick={() => setChatOpen(false)}>_</button>
                    </div>
                </div>
                <div className="chat-messages" ref={messagesContainerRef}>
                    {messages.map((msg, index) => {
                        const isOwn = user && msg.user_id === user.id;
                        return (
                            <div key={msg.id || index}
                                 className={`chat-message ${isOwn ? 'own-message' : 'other-message'}`}>
                                {editingMessage === msg.id ? (
                                    <div className="edit-message-container">
                                        <textarea
                                            value={editedText}
                                            onChange={(e) => setEditedText(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    handleUpdateMessage(msg.id);
                                                }
                                            }}
                                        />
                                        <button onClick={() => handleUpdateMessage(msg.id)} className='button submit-button edit-message-container-button margin-right-button-small'>Save</button>
                                        <button onClick={() => setEditingMessage(null)} className='button cancel-button edit-message-container-button'>Cancel</button>
                                    </div>
                                ) : (
                                    <>
                                        {msg.text}
                                        {isOwn && (
                                            <div className="message-actions">
                                                <button onClick={() => setActiveDropdown(activeDropdown === msg.id ? null : msg.id)} className="three-dots">
                                                    ...
                                                </button>
                                                {activeDropdown === msg.id && (
                                                    <div className="dropdown-menu">
                                                        <button onClick={() => handleEditClick(msg)}>Edit</button>
                                                        <button onClick={() => handleDeleteMessage(msg.id)}>Delete</button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )
                    })}
                    <div ref={messagesEndRef}/>
                </div>
                {selectedChat && (
                    <div className="chat-input">
                        <textarea
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSendMessage();
                                }
                            }}
                            placeholder="Type a message..."
                        />
                        <button onClick={handleSendMessage}>Send</button>
                    </div>
                )}
            </div>
            {isCreateGroupChatModalOpen && (
                <CreateGroupChatModal
                    isOpen={isCreateGroupChatModalOpen}
                    onClose={() => setCreateGroupChatModalOpen(false)}
                    user={user}
                    onChatCreated={handleChatCreated}
                />
            )}
            {isEditGroupChatModalOpen && (
                <EditGroupChatModal
                    isOpen={isEditGroupChatModalOpen}
                    onClose={() => setEditGroupChatModalOpen(false)}
                    user={user}
                    chat={selectedChat}
                    onChatUpdated={handleChatUpdated}
                />
            )}
        </div>
    );
};

export default Chat;
